/**
 * Utility functions for handling RBAC API errors
 */

/**
 * Type guard to check if a value is a record (object)
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/**
 * Translation map for common backend error messages to Spanish
 */
const ERROR_TRANSLATIONS: Record<string, string> = {
  'Message rejected: Invalid phone number': 'Mensaje rechazado: Número de teléfono inválido',
  'Message rejected: Client was contacted less than 24 hours ago':
    'Mensaje rechazado: El cliente fue contactado hace menos de 24 horas',
  'Message rejected: AI is disabled for this client': 'Mensaje rechazado: La IA está desactivada para este cliente',
  'Invalid phone number': 'Número de teléfono inválido',
};

/**
 * Translates error messages from English to Spanish if a translation is available
 * @param message - The error message to translate
 * @returns The translated message or the original if no translation exists
 */
function translateErrorMessage(message: string): string {
  return ERROR_TRANSLATIONS[message] || message;
}

/**
 * Extracts an error message from various error shapes that can come from RTK Query or API responses
 * @param error - The error object (can be various shapes from RTK Query errors)
 * @param fallback - Fallback message to use if no error message can be extracted
 * @returns The extracted error message or the fallback, translated to Spanish if available
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === 'string' && error.trim()) return translateErrorMessage(error);

  if (isRecord(error)) {
    // Prefer nested API error shapes (common in RTK Query error responses)
    const { data, message, error: errorField } = error as { data?: unknown; message?: unknown; error?: unknown };
    if (
      Array.isArray(data) &&
      data.length > 0 &&
      data.every((issue) => isRecord(issue) && typeof issue.message === 'string')
    ) {
      return data.map((issue) => translateErrorMessage(issue.message as string)).join('. ');
    }
    if (isRecord(data)) {
      if (typeof data?.message === 'string') return translateErrorMessage(data.message);
      if (typeof data?.error === 'string') return translateErrorMessage(data.error);
    }
    // Raw text body (e.g. Koa text/plain errors). Prefer it over `errorField`, which on a
    // PARSING_ERROR holds the client-side JSON parse exception, not the server's actual message.
    if (typeof data === 'string' && data.trim()) return translateErrorMessage(data);
    // Check top-level error field
    if (typeof errorField === 'string' && errorField.trim()) return translateErrorMessage(errorField);
    // Fallback to top-level message when available
    if (typeof message === 'string' && message.trim()) return translateErrorMessage(message);
  }

  return fallback;
}

/**
 * Default error messages for RBAC operations
 */
export const DEFAULT_RBAC_ERRORS = {
  role: {
    load: 'Error al cargar el rol. Por favor, intenta nuevamente.',
    create: 'Error al crear el rol. Por favor, intenta nuevamente.',
    update: 'Error al actualizar el rol. Por favor, intenta nuevamente.',
    delete: 'Error al eliminar el rol. Por favor, intenta nuevamente.',
    list: 'Error al cargar los roles. Por favor, intenta nuevamente.',
  },
  permission: {
    load: 'Error al cargar el permiso. Por favor, intenta nuevamente.',
    create: 'Error al crear el permiso. Por favor, intenta nuevamente.',
    update: 'Error al actualizar el permiso. Por favor, intenta nuevamente.',
    delete: 'Error al eliminar el permiso. Por favor, intenta nuevamente.',
    list: 'Error al cargar los permisos. Por favor, intenta nuevamente.',
  },
  user: {
    load: 'Error al cargar la información del usuario. Por favor, intenta nuevamente.',
    assignRole: 'Error con la asignación del rol.',
    removeRole: 'Error al remover el rol del usuario.',
  },
} as const;
